import React, { useEffect, useState } from 'react'
import { QuilttProvider } from '@quiltt/react'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { useIntake } from 'hooks/useIntakeContext'
import { useApi } from 'hooks/useApi'

const QuilttConnectProvider = () => {
  const { intakeState, updateIntakeStateAtKey } = useIntake()
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const { apiRequest } = useApi()

  const [sessionToken, setSessionToken] = useState(null)
  const [quilttUserId, setQuilttUserId] = useState(null)
  const [quilttSessionCreationDate, setQuilttSessionCreationDate] =
    useState(null)
  const [hasFetchedSession, setHasFetchedSession] = useState(false)
  const [retrying, setRetrying] = useState(false)

  // Sync state from intake once loaded
  useEffect(() => {
    if (!intakeState) return
    setSessionToken(intakeState?.quilttSessionToken || null)
    setQuilttUserId(intakeState?.quilttUserId || null)
    setQuilttSessionCreationDate(intakeState?.quilttSessionCreationDate || null)
  }, [
    intakeState?.quilttSessionToken,
    intakeState?.quilttUserId,
    intakeState?.quilttSessionCreationDate,
  ])

  const updateUserWithQuilttData = (
    quilttUserId,
    sessionToken,
    sessionDate
  ) => {
    if (!intakeState?.id || intakeState.id === 'undefined') {
      console.warn(
        '⛔️ Skipping Quiltt update — intakeState has no valid user ID yet'
      )
      return
    }
    updateIntakeStateAndSubmit(() => {
      const newState = updateIntakeStateAtKey({
        quilttUserId,
        quilttSessionToken: sessionToken,
        quilttSessionCreationDate: sessionDate,
      })
      return newState
    })()
  }

  useEffect(() => {
    if (hasFetchedSession) return
    if (!intakeState) return

    if (!intakeState.id || intakeState.id === 'undefined') {
      console.warn('⛔️ Skipping Quiltt fetch — no valid intakeState.id yet')
      return
    }

    // ✅ guard: only trigger once per mount/session
    setHasFetchedSession(true)

    const isSameDay = (dateString) => {
      if (!dateString) return false
      const today = new Date()
      const date = new Date(dateString)
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      )
    }

    const shouldFetchNewSession =
      !sessionToken ||
      !quilttSessionCreationDate ||
      !isSameDay(quilttSessionCreationDate)

    if (!shouldFetchNewSession) return

    const fetchToken = async (isRetry = false) => {
      try {
        if (!intakeState?.id || intakeState.id === 'undefined') {
          console.warn(
            '⛔️ Aborting Quiltt token fetch — invalid user ID at call time'
          )
          return
        }
        const res = await apiRequest('/api/quiltt/quilttCreateSession', {
          method: 'POST',
          body: JSON.stringify({
            quilttUserId: quilttUserId || intakeState?.quilttUserId,
          }),
        })

        if (res.status === 401 && !isRetry) {
          console.warn('401 from Quiltt — retrying in 1s...')
          setRetrying(true)
          setTimeout(() => fetchToken(true), 1000)
          return
        }

        if (res.status === 401 && isRetry) {
          console.error('Second 401 — aborting Quiltt session creation')
          return
        }

        const data = await res.json()
        if (!res.ok || !data.sessionToken) {
          throw new Error(data.message || 'Failed to fetch session token')
        }

        setSessionToken(data.sessionToken)
        setQuilttUserId(data.quilttUserId)
        setQuilttSessionCreationDate(data.quilttSessionCreationDate)

        updateUserWithQuilttData(
          data.quilttUserId,
          data.sessionToken,
          data.quilttSessionCreationDate
        )
      } catch (error) {
        console.error('Error fetching Quiltt session token:', error)
      }
    }

    fetchToken()
  }, [
    intakeState, // only check once when ready
    sessionToken,
    quilttUserId,
    quilttSessionCreationDate,
    hasFetchedSession,
    retrying,
    apiRequest,
  ])

  if (!intakeState) return null
  if (!sessionToken) return null

  return <QuilttProvider token={sessionToken}></QuilttProvider>
}

export default QuilttConnectProvider
